import { Component, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { Collection } from '../../../../models/collections/collection.model';
import { ApiCollectionsService } from '../../../../services/collections/api/api-collections.service';
import { AlertsService } from '../../../../services/alerts.service';
import { ModalsService } from '../../../../services/modals.service';
import { LanguagesService } from '../../../../services/languages/languages.service';
import { getMissingTranslationsCount } from '../../collection-translation-form.model';
import { CollectionsImagesHelper } from '../../../../utils/helpers/collections-images.helper';
import { CollectionAddModalComponent } from '../../modals/collection-add-modal/collection-add-modal.component';
import { CollectionEditModalComponent } from '../../modals/collection-edit-modal/collection-edit-modal.component';
import { UserCurrencyType } from '../../../../enums/users/user-currency-type.enum';

@Component({
  selector: 'app-collections-list-tab',
  templateUrl: './collections-list-tab.component.html',
  standalone: true,
  imports: [CommonModule, RouterModule, TooltipModule, CollectionAddModalComponent, CollectionEditModalComponent]
})
export class CollectionsListTabComponent {

  @ViewChild('addModal') addModal!: CollectionAddModalComponent;
  @ViewChild('editModal') editModal!: CollectionEditModalComponent;
  @ViewChild('imageInput') imageInput!: ElementRef<HTMLInputElement>;

  collections: Collection[] = [];
  isLoading: boolean = false;
  uploadingImageId: number = 0;

  private imageUploadTarget: Collection | null = null;
  totalResults: number = 0;
  readonly resultsPerPage: number = 25;

  private static pageNumber: number = 1;

  get pageNumber(): number { return CollectionsListTabComponent.pageNumber; }
  get totalPages(): number { return Math.ceil(this.totalResults / this.resultsPerPage); }

  constructor(
    private apiCollectionsService: ApiCollectionsService,
    private alertsService: AlertsService,
    private modalsService: ModalsService,
    private languagesService: LanguagesService
  ) {}

  getPageNumbers(): (number | null)[] {
    const total = this.totalPages;
    const current = this.pageNumber;
    const visibleSet = new Set<number>();
    for (let i = 1; i <= Math.min(2, total); i++) visibleSet.add(i);
    for (let i = Math.max(total - 1, 1); i <= total; i++) visibleSet.add(i);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) visibleSet.add(i);
    const sorted = Array.from(visibleSet).sort((a, b) => a - b);
    const result: (number | null)[] = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
      result.push(sorted[i]);
    }
    return result;
  }

  goToPage(page: number): void { CollectionsListTabComponent.pageNumber = page; this.refresh(); }
  onClickPaginationPrevious(): void { CollectionsListTabComponent.pageNumber--; this.refresh(); }
  onClickPaginationNext(): void { CollectionsListTabComponent.pageNumber++; this.refresh(); }

  getMissingTranslationsCount(collection: Collection): number {
    return getMissingTranslationsCount(this.languagesService.languages, collection.translations);
  }

  hasImage(collection: Collection): boolean {
    return CollectionsImagesHelper.hasImage(collection.imageUpdatedAt);
  }

  getImageUrl(collection: Collection): string {
    return CollectionsImagesHelper.getImageUrl('collection', collection.collectionId, collection.imageUpdatedAt);
  }

  onClickUploadImage(collection: Collection): void {
    this.imageUploadTarget = collection;
    this.imageInput.nativeElement.click();
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const collection = this.imageUploadTarget;
    input.value = '';
    this.imageUploadTarget = null;
    if (!file || !collection) return;
    const fileError = CollectionsImagesHelper.getImageFileError(file);
    if (fileError) {
      this.alertsService.displayAlertError(fileError);
      return;
    }
    this.uploadingImageId = collection.collectionId;
    this.apiCollectionsService.uploadCollectionImage(collection.collectionId, file).then(
      () => {
        this.uploadingImageId = 0;
        this.alertsService.displayAlertSuccess('Image uploaded !');
        this.refresh();
      }, error => {
        this.uploadingImageId = 0;
        this.alertsService.displayAlertError('Error uploading image : ' + error);
      }
    );
  }

  onClickAdd(): void {
    this.addModal.show(() => this.refresh());
  }

  onClickEdit(collection: Collection): void {
    this.editModal.show(collection, () => this.refresh());
  }

  onClickDelete(collection: Collection): void {
    this.modalsService.showConfirmModal(() => {
      this.apiCollectionsService.deleteCollection(collection.collectionId).then(
        () => {
          this.alertsService.displayAlertSuccess('Collection deleted !');
          this.refresh();
        }, error => {
          this.alertsService.displayAlertError('Error deleting collection : ' + error);
        }
      );
    });
  }

  refresh(): void {
    this.isLoading = true;
    this.apiCollectionsService.getCollections(this.pageNumber, this.resultsPerPage).then(
      response => {
        this.isLoading = false;
        this.collections = response.results;
        this.totalResults = response.resultsTotal;
        if (this.pageNumber > this.totalPages && this.totalPages > 0) {
          CollectionsListTabComponent.pageNumber = 1;
          this.refresh();
        }
      }, error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error loading collections : ' + error);
      }
    );
  }

  getCurrencyTypeName(type: number): string { return UserCurrencyType[type] ?? String(type); }
}
